"use client";

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Toolbar from "./Toolbar";
import { customCommands } from "./commands/customCommands";
import { registerInlineFormat } from "./commands/inlines/inlines.helper";
import { createEditor } from "./core";
import { createPNodeAfterBlockQuote } from "./special-action/createBlockQuote.helper";
import { handlePaste } from "./special-action/paste.helper";
import { BodyEditor, Label } from "./style";
import { validate } from "./validation/validate";
import { normalizeParagraph } from './normalize';

const Editor = forwardRef(
  (
    {
      value = null,
      onChange,
      options,
      required,
      rules = [],
      label,
      title,
      widthLabel,
      heightEditor,
      widthEditor,
      resetKey,
    },
    ref,
  ) => {
    const rootRef = useRef(null);
    const editorRef = useRef(null);
    const [error, setError] = useState(null);

    useEffect(() => {
      if (!rootRef.current) return;

      const rootElement = rootRef.current;

      const editor = createEditor({
        root: rootElement,
        history: true,
        commands: true,
        normalize: true,
      });

      // Built-in commands with empty state handling
      registerInlineFormat({ editor, commandName: 'bold', tagName: 'b', execCommandName: 'bold' });
      registerInlineFormat({
        editor,
        commandName: 'italic',
        tagName: 'i',
        execCommandName: 'italic',
      });
      registerInlineFormat({
        editor,
        commandName: 'strike',
        tagName: 'strike',
        execCommandName: 'strikeThrough',
      });

      editor.commands?.register('link', (url) => {
        if (url) {
          document.execCommand('createLink', false, url);
        }
      });

      // Custom commands
      editor.commands?.register('heading', () => customCommands.heading(editor));
      editor.commands?.register('quote', () => customCommands.quote(editor));
      editor.commands?.register('bulletList', () => customCommands.bulletList(editor));
      editor.commands?.register('orderedList', () => customCommands.orderedList(editor));
      editor.commands?.register('indent', () => customCommands.indent(editor));
      editor.commands?.register('outdent', () => customCommands.outdent(editor));
      editor.commands?.register('foreColor', (color) =>
        customCommands.foreColorVer2(editor, color),
      );

      // History
      editor.commands?.register('undo', () => editor.history?.undo());
      editor.commands?.register('redo', () => editor.history?.redo());

      editorRef.current = editor;
    }, []);

    /**
     * ---------------------------
     * SYNC VALUE -> DOM
     * ---------------------------
     */
    useEffect(() => {
      if (!rootRef.current) return;
      if (rootRef.current.innerHTML !== value) {
        rootRef.current.innerHTML = value || '<p><br></p>';

        // Normalize after setting value to clean up any messy HTML
        if (editorRef.current?.normalize && value) {
          editorRef.current.normalize();
        }

        // Normalize paragraphs on init
        normalizeParagraph(rootRef.current);

        // Reinit snapshot after value is set (when resetKey changes)
        if (resetKey && resetKey > 0) {
          setTimeout(() => {
            editorRef.current?.history?.reinit({ html: value || '<p><br></p>' });
            window.dispatchEvent(new CustomEvent('editor-history-change'));
          }, 50);
        }
      }
    }, [value, resetKey]);

    /**
     * ---------------------------
     * VALIDATE ON SUBMIT
     * ---------------------------
     */
    const validateOnSubmit = async () => {
      if (!rootRef.current) return false;

      const htmlContent = rootRef.current.innerHTML;

      const validationError = await validate({
        val: htmlContent,
        rules,
        trigger: 'onSubmit',
        skipRequired: false,
        compOnChange: false,
      });

      if (validationError) {
        setError(validationError);
        return false;
      }

      setError(null);
      return true;
    };

    /**
     * ---------------------------
     * CLEANUP ON UNMOUNT
     * ---------------------------
     */
    useEffect(() => {
      return () => {
        // Reset error when editor is unmounted/closed
        setError(null);
      };
    }, [resetKey]);
    useImperativeHandle(ref, () => ({
      validate: validateOnSubmit,
      getHTML: () => rootRef.current?.innerHTML || '',
      clear: () => {
        if (rootRef.current) rootRef.current.innerHTML = '';
      },
      initSnapshot: () => {
        editorRef.current?.history?.takeSnapshot();
        window.dispatchEvent(new CustomEvent('editor-history-change'));
      },
      reinitSnapshot: ({ html } = {}) => {
        if (html !== undefined) {
          if (rootRef.current) {
            rootRef.current.innerHTML = html || '<p><br></p>';
          }
          editorRef.current?.history?.reinit({ html });
        } else {
          const currentHTML = rootRef.current?.innerHTML || '<p><br></p>';
          editorRef.current?.history?.reinit({ html: currentHTML });
        }
        window.dispatchEvent(new CustomEvent('editor-history-change'));
      },
      reset: () => {
        if (rootRef.current) {
          rootRef.current.innerHTML = '<p><br></p>';
        }
        // Reset history to initial state with empty HTML
        editorRef.current?.history?.reinit({ html: '<p><br></p>' });
        window.dispatchEvent(new CustomEvent('editor-history-change'));
      },
    }));

    /**
     * ---------------------------
     * EVENTS
     * ---------------------------
     */
    useEffect(() => {
      const rootElement = rootRef.current;
      if (!rootElement) return;

      const handleInput = async () => {
        // Don't schedule snapshot if we're restoring from history
        if (!editorRef.current?.history?.isRestoring) {
          editorRef.current?.history?.scheduleSnapshot();
        }

        if (editorRef.current?.normalize) {
          editorRef.current.normalize();
        }

        // Normalize paragraphs after input
        normalizeParagraph(rootElement);

        const content = rootElement.innerHTML;

        // emit ra ngoài
        onChange?.({ label: title, value: content });

        // Dispatch custom event for toolbar to update history state
        window.dispatchEvent(new CustomEvent('editor-history-change'));

        // realtime validate
        if (rules.length > 0) {
          const validationError = await validate({
            val: content,
            rules,
            trigger: 'onChange',
            skipRequired: false,
            compOnChange: true,
          });
          setError(validationError);
        }
      };

      const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
          console.log('enter ...');
          createPNodeAfterBlockQuote(rootElement, e);
        }

        // Ctrl + Z: Undo
        if (e.ctrlKey && e.key.toLowerCase() === 'z' && !e.shiftKey) {
          e.preventDefault();
          editorRef.current?.commands?.execute('undo');
        }

        // Ctrl + Shift + Z or Ctrl + Y: Redo
        if (
          (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'z') ||
          (e.ctrlKey && e.key.toLowerCase() === 'y')
        ) {
          e.preventDefault();
          editorRef.current?.commands?.execute('redo');
        }
      };

      const handlePasteEvent = (e) => {
        handlePaste(e, editorRef.current);
      };

      const handleClick = (e) => {
        const target = e.target;
        // Check if target is an <a> tag or if it's inside an <a> tag
        const link = target.tagName === 'A' ? target : target.closest('a');
        if (link) {
          e.preventDefault();
          const href = link.getAttribute('href');
          if (href) window.open(href, '_blank');
        }
      };

      rootElement.addEventListener('input', handleInput);
      rootElement.addEventListener('keydown', handleKeyDown);
      rootElement.addEventListener('paste', handlePasteEvent);
      rootElement.addEventListener('click', handleClick);

      return () => {
        rootElement.removeEventListener('input', handleInput);
        rootElement.removeEventListener('keydown', handleKeyDown);
        rootElement.removeEventListener('paste', handlePasteEvent);
        rootElement.removeEventListener('click', handleClick);
      };
    }, [onChange, rules, rootRef.current]);

    return (
      <div className="w-full h-max flex gap-4 pb-2 bg-transparent">
        <Label $widthLabel={widthLabel}>
          {label}
          {required && <p style={{ color: 'red' }}>*</p>}
        </Label>

        <BodyEditor $heightEditor={heightEditor} $widthEditor={widthEditor}>
          <Toolbar toolbar={options} editorRef={editorRef} />

          <div
            style={{
              width: '100%',
              height: `${heightEditor}px`,
            }}
          >
            <div
              ref={rootRef}
              contentEditable
              suppressContentEditableWarning
              className={`w-full h-full p-4 outline-none bg-white text-gray-800 overflow-y-auto [&_a]:text-blue-600 [&_a]:cursor-pointer [&_a:hover]:underline border rounded-md ${
                error ? 'border-red-500' : 'border-[#ddd]'
              }`}
            />

            {error && <div className="text-red-500 text-sm mt-1 min-h-12">{error}</div>}
          </div>
        </BodyEditor>
      </div>
    );
  },
);

export default Editor;